import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  BrowserMockFrame,
  buildRegistrationHref,
  ProductScreenshot,
  PublicLpFooter,
  PublicLpHeader,
  SectionIntro,
} from "../components/public/HypothesisLandingParts";
import {
  ProductCatalogContentUnavailable,
  type ProductCatalogViewState,
} from "../components/public/ProductCatalogContent";
import { useProductCatalog } from "../hooks/useProductCatalog";
import { getProductCatalogPlan, getProductCatalogQuotaDisplayLabel } from "../lib/productCatalog";
import { buildPublicLpDestinationHref, buildPublicLpReturnTo } from "../lib/publicLpReturn";
import { PUBLIC_PAGE_METADATA } from "../publicPageMetadata";

const SALES_SUPPORT_METADATA = PUBLIC_PAGE_METADATA.salesSupport;

const PAINS = [
  {
    number: "01",
    title: "会社サイトを何ページも見る",
    body: "会社概要、サービス、最近の情報。ブラウザのタブだけが増えていく。",
  },
  {
    number: "02",
    title: "相手に何を話すか考える",
    body: "自社の商品やサービスは分かっていても、「この会社に何をどう提案するか」は毎回考える必要があります。",
  },
  {
    number: "03",
    title: "提案資料と企業情報を見比べる",
    body: "相手の状況を調べながら、過去の提案書やサービス資料を探し直す。",
  },
  {
    number: "04",
    title: "最後は一人で判断する",
    body: "「この話で刺さるだろうか」。相談したくても、必要なときに社内で相談相手が見つかるとは限りません。",
  },
] as const;

const KNOWLEDGE_ITEMS = [
  {
    title: "PDF",
    lead: "会社案内、サービス資料、提案書などのPDFを登録。",
    body: "相手企業の情報と照らしながら、自社のどこが提案につながりそうかを考えます。",
  },
  {
    title: "社長メモ",
    lead: "紙でもらった資料やパンフレット、手書きのメモは、スマホで撮影して「社長メモ」に。",
    body: "外出先で撮った情報もWizMateへ残し、あとから企業分析や壁打ちの材料として使います。",
  },
  {
    title: "Webページ",
    lead: "自社サイトや参考にしたいWebページも登録。",
    body: "資料・メモ・Webの情報を、AIへ渡す営業準備の材料としてまとめます。",
  },
] as const;

const KNOWLEDGE_FLOW_ITEMS = [
  ["企業リサーチ", "詳しく調べた結果をナレッジライブラリへ残し、AI壁打ちの材料として再利用できます。"],
  ["自社ナレッジ", "提案書や社長メモなど、自社側の情報を企業分析やAI壁打ちへ加えます。"],
  ["AI壁打ち", "修正した提案書も材料にして、違う立場から意見を聞きます。"],
] as const;

const PRESIDENT_ROOM_FEATURES = [
  "日々のAI相談",
  "AI壁打ち",
  "企業リサーチ",
  "ナレッジライブラリ",
  "ニュース・レポート",
  "カレンダー予定確認",
] as const;

function HeroWorkflowVisual() {
  const steps = [
    { label: "調べる", body: "相手企業を企業リサーチ", accent: "bg-[#F4DED0] text-[#8A4F35]" },
    { label: "考える", body: "この会社にどう提案する？", accent: "bg-[#E9E2C8] text-[#695C2D]" },
    { label: "壁打ち", body: "役割・立場を変えて意見をもらう", accent: "bg-[#DCE8E2] text-[#3D6958]" },
  ] as const;

  return (
    <div className="relative rounded-[2rem] border border-[#DEC7B6] bg-white/90 p-4 shadow-[0_26px_60px_rgba(112,72,52,0.18)] sm:p-6">
      <div className="flex items-center gap-2 border-b border-[#EEE1D5] pb-4">
        <span className="h-2.5 w-2.5 rounded-full bg-[#D9826F]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#E5B96C]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#82AE92]" />
        <span className="ml-2 text-xs font-black tracking-[0.12em] text-[#8A756B]">SALES PREPARATION</span>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-stretch">
        {steps.map((step, index) => (
          <div key={step.label} className="contents">
            <div className="rounded-2xl border border-[#E9D9CC] bg-[#FFFDFC] p-4">
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${step.accent}`}>{step.label}</span>
              <p className="mt-3 text-sm font-black leading-6 text-[#4E3830]">{step.body}</p>
            </div>
            {index < steps.length - 1 ? (
              <span aria-hidden className="flex items-center justify-center text-xl font-black text-[#C09075] sm:rotate-0 rotate-90">→</span>
            ) : null}
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-2xl bg-[#6A4A3E] p-5 text-white">
        <p className="text-xs font-black tracking-[0.16em] text-[#F0CDBB]">AI社長室 WizMate</p>
        <p className="mt-2 text-sm font-bold leading-7">企業調査から提案検討、壁打ちまでを、一連の営業準備としてサポートします。</p>
      </div>
    </div>
  );
}

function CandidateMock() {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-bold text-[#9A7968]">企業名</p>
        <div className="mt-2 flex gap-2">
          <div className="min-w-0 flex-1 rounded-xl border border-[#DCC6B4] bg-white px-4 py-3 text-sm font-bold text-[#55413A]">株式会社サンプル</div>
          <span className="inline-flex items-center rounded-xl bg-[#8A4F35] px-4 text-xs font-black text-white">候補を探す</span>
        </div>
      </div>
      <div className="rounded-2xl border-2 border-[#C88766] bg-[#FFF9F5] p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-black text-[#4E3830]">株式会社サンプル</p>
            <p className="mt-1 text-xs text-[#7A655C]">東京都・sample.example.jp</p>
          </div>
          <span className="rounded-full bg-[#E7F0EA] px-2.5 py-1 text-[10px] font-black text-[#3D6958]">候補を確認</span>
        </div>
        <div className="mt-4 inline-flex min-h-10 items-center rounded-xl bg-[#8A4F35] px-4 text-xs font-black text-white">この企業で概要を見る</div>
      </div>
    </div>
  );
}

function ResearchMock() {
  return (
    <div className="grid gap-4 md:grid-cols-[0.75fr_1.25fr]">
      <div className="rounded-2xl bg-[#F7EEE7] p-4">
        <p className="text-xs font-black text-[#A15F42]">企業情報の確認</p>
        <p className="mt-2 font-black text-[#4E3830]">株式会社サンプル</p>
        <dl className="mt-4 space-y-3 text-xs">
          <div><dt className="font-bold text-[#92786C]">公式サイト</dt><dd className="mt-1 text-[#55413A]">sample.example.jp</dd></div>
          <div><dt className="font-bold text-[#92786C]">事業</dt><dd className="mt-1 leading-5 text-[#55413A]">業務支援サービスを提供</dd></div>
        </dl>
      </div>
      <div className="rounded-2xl border border-[#E7D5C7] p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="font-black text-[#4E3830]">詳しく調べる</p>
          <span className="rounded-full bg-[#F2DCCF] px-2.5 py-1 text-[10px] font-black text-[#8A4F35]">おすすめ</span>
        </div>
        <div className="mt-4 space-y-2">
          {["事業の特徴と最近の動き", "商談前に押さえたい論点", "公開情報から見える取り組み"].map((item) => (
            <div key={item} className="flex gap-2 rounded-xl bg-[#FAF6F2] px-3 py-2.5 text-xs font-bold text-[#65514A]">
              <span className="text-[#B56F4E]">✓</span>{item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WorkflowStep({
  number,
  title,
  children,
  visual,
  reverse = false,
}: {
  number: string;
  title: string;
  children: ReactNode;
  visual: ReactNode;
  reverse?: boolean;
}) {
  return (
    <article className="grid items-center gap-8 rounded-[2rem] border border-[#E8D8CC] bg-white/75 p-5 shadow-[0_16px_42px_rgba(102,72,56,0.09)] md:p-8 lg:grid-cols-2 lg:gap-12">
      <div className={reverse ? "lg:order-2" : ""}>
        <p className="text-xs font-black tracking-[0.2em] text-[#B06E4F]">STEP {number}</p>
        <h3 className="mt-3 text-2xl font-black leading-snug text-[#4E3830] md:text-3xl">{title}</h3>
        <div className="mt-5 space-y-4 text-base leading-8 text-[#6B5851]">{children}</div>
      </div>
      <div className={reverse ? "lg:order-1" : ""}>{visual}</div>
    </article>
  );
}

export function SalesSupportPageView({
  catalog,
  catalogLoading = false,
  retryCatalog,
}: ProductCatalogViewState) {
  const location = useLocation();
  const registrationHref = buildRegistrationHref(location.search);
  const returnTo = buildPublicLpReturnTo(location.pathname, location.search);
  const freePlan = catalog ? getProductCatalogPlan(catalog, "free") : null;
  const standardPlan = catalog ? getProductCatalogPlan(catalog, "standard") : null;
  const freeCompanyResearchQuota = catalog
    ? getProductCatalogQuotaDisplayLabel(catalog, "free", "company_research", "deep_dive_monthly")
    : null;
  const standardCompanyResearchQuota = catalog
    ? getProductCatalogQuotaDisplayLabel(catalog, "standard", "company_research", "deep_dive_monthly")
    : null;
  const pricingReady = Boolean(
    freePlan && standardPlan && freeCompanyResearchQuota && standardCompanyResearchQuota
  );

  useEffect(() => {
    document.title = SALES_SUPPORT_METADATA.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    description?.setAttribute("content", SALES_SUPPORT_METADATA.description);
  }, []);

  return (
    <div className="h-dvh overflow-x-hidden overflow-y-auto scroll-smooth bg-[#FDF8F5] text-[#4E3830]">
      <PublicLpHeader registrationHref={registrationHref} />
      <main>
        <section className="relative overflow-hidden">
          <div aria-hidden className="absolute -left-24 top-8 h-72 w-72 rounded-full bg-[#F3D8C8]/60 blur-3xl" />
          <div aria-hidden className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[#DEEAE3]/70 blur-3xl" />
          <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-12 md:px-8 md:pb-24 md:pt-20 lg:grid-cols-[1.02fr_0.98fr]">
            <div>
              <p className="inline-flex rounded-full border border-[#DCB9A6] bg-white/70 px-4 py-2 text-xs font-black tracking-[0.12em] text-[#9A563A]">商談準備を、一つの流れに</p>
              <h1 className="mt-6 text-4xl font-black leading-[1.25] tracking-tight text-[#49332B] md:text-[2.75rem] lg:text-[2.85rem] xl:text-[3rem]">
                <span className="md:block md:whitespace-nowrap">商談前の</span>
                <span className="md:block md:whitespace-nowrap">「調べる・考える」を、</span>
                <span className="text-[#A05F3F] md:block md:whitespace-nowrap">AIと一緒に。</span>
              </h1>
              <div className="mt-6 space-y-3 text-base leading-8 text-[#68554D] md:text-lg">
                <p>相手企業をWeb上の公開情報から調べて整理。</p>
                <p>自社の提案書やサービス資料も踏まえて、「この会社に何をどう提案するか」を考えます。</p>
                <p>最後は、役割や立場を変えたAIと壁打ち。</p>
                <p>商談で聞かれそうなこと、提案の弱いところ、先に確認したいことを<strong className="text-[#4E3830]">本番前に予習できます。</strong></p>
              </div>
              <p className="mt-6 border-l-4 border-[#C27A58] pl-4 text-sm font-black leading-7 text-[#5A453D]">WizMateは、社長の仕事を支える「AI社長室」です。</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link to={registrationHref} className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-[#8A4F35] px-7 text-base font-black text-white shadow-[0_14px_30px_rgba(138,79,53,0.25)] transition hover:bg-[#74412C] focus:outline-none focus:ring-2 focus:ring-[#C58B6B] focus:ring-offset-2">無料でWizMateを試す</Link>
                <a href="#workflow" className="inline-flex min-h-12 items-center justify-center px-4 text-sm font-black text-[#8A4F35] underline decoration-[#D0A18A] underline-offset-4">営業準備で何ができるか見る ↓</a>
              </div>
            </div>
            <HeroWorkflowVisual />
          </div>
        </section>

        <section className="border-y border-[#EADBC8] bg-[#F8EFE8]">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro eyebrow="BEFORE THE MEETING" title="商談前、こんな準備に時間を取られていませんか？" align="center" />
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {PAINS.map((pain) => (
                <article key={pain.number} className="rounded-[1.5rem] border border-[#E1CFC1] bg-white p-5 shadow-[0_12px_28px_rgba(102,72,56,0.07)]">
                  <span className="text-xs font-black text-[#BC7A5B]">{pain.number}</span>
                  <h3 className="mt-3 text-lg font-black leading-7 text-[#4E3830]">{pain.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-[#705D55]">{pain.body}</p>
                </article>
              ))}
            </div>
            <p className="mt-9 text-center text-lg font-black text-[#5D4037]">WizMateは、この商談準備を一つの流れにまとめます。</p>
          </div>
        </section>

        <section id="workflow" className="scroll-mt-20">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro eyebrow="3 STEPS" title="相手を調べて、提案を考えて、商談を予習する。" align="center">
              <p>提案先の企業を知り、調査結果をナレッジへ残す。AI壁打ちで提案方針を整理し、自分たちで提案書を作り、必要ならその提案書でもう一度AI壁打ちする。WizMateを使った営業準備の流れです。</p>
            </SectionIntro>
            <div className="mt-12 space-y-7">
              <WorkflowStep number="1" title="商談する会社を調べる" visual={<BrowserMockFrame title="企業リサーチ" caption="会社名から候補を探し、今回の対象企業を確認します。"><CandidateMock /></BrowserMockFrame>}>
                <p>会社名を入力すると、WizMateがWeb上の公開情報を探して整理します。</p>
                <p>同じ名前の会社があれば候補を確認。<strong className="text-[#4E3830]">今回の対象企業を選んで、企業情報を確認します。</strong></p>
              </WorkflowStep>
              <WorkflowStep number="2" title="相手企業をもう一歩深く知る" reverse visual={<BrowserMockFrame title="企業情報の確認 → 詳しく調べる" caption="確認した企業を起点に、目的別分析の土台を整理します。"><ResearchMock /></BrowserMockFrame>}>
                <p>対象企業を確認したら、さらに詳しく調査。</p>
                <p>事業内容や外部の公開情報をまとめ、<strong className="text-[#4E3830]">「この会社に何をどう提案するか」を考える材料を集めます。</strong></p>
                <p>詳しく調べた企業リサーチ結果は、ナレッジライブラリへ追加できます。</p>
              </WorkflowStep>
              <WorkflowStep number="3" title="切り口を整理して、提案書を見直す" visual={<ProductScreenshot src="/img/sales-support-step3-analysis.jpg" alt="企業分析結果。相手企業の課題仮説、刺さりそうな提案切り口、初回商談で聞くべき質問を表示" width={950} height={746} caption="企業リサーチをもとに、営業で考えるべき課題・提案切り口・質問を整理します。" frameTitle="企業分析結果" />}>
                <p>ナレッジに残した企業リサーチ結果を材料にAI壁打ちし、「この会社に何をどう提案するか」を整理します。</p>
                <p>課題仮説、提案の切り口、初回商談で聞く質問を参考に、<strong className="text-[#4E3830]">自分たちで提案書を作成・見直します。</strong></p>
                <p>必要なら、作成・修正した提案書もPDFとしてナレッジライブラリへ登録し、もう一度AI壁打ちの材料にできます。</p>
                <p>調査結果や実際の提案内容をもとに、</p>
                <ul className="list-disc space-y-1 pl-6">
                  <li>この切り口で伝わるか</li>
                  <li>説明が弱いところはないか</li>
                  <li>相手から何を聞かれそうか</li>
                  <li>先に確認することはないか</li>
                </ul>
                <p>を違う立場から検討します。</p>
              </WorkflowStep>
            </div>
          </div>
        </section>

        <section id="kabeuchi" className="scroll-mt-20 bg-[#5C443A] text-white">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-xs font-black tracking-[0.2em] text-[#F0BEA5]">AI KABEUCHI</p>
              <h2 className="mt-3 text-3xl font-black leading-tight md:text-4xl">調査結果や提案書を材料に、<span className="lg:block">AI相手に壁打ち。</span></h2>
              <div className="mt-6 space-y-4 text-base leading-8 text-[#F3E7E0]">
                <p>壁打ちは、自分の考えを相手にぶつけて、別の立場から意見をもらうこと。</p>
                <p>企業リサーチ結果から提案方針を考えるときも、作成・修正した提案書を見直すときも使えます。</p>
                <p>WizMateでは、役割やスタンスを変えながら、同じ提案を違う角度から見直します。</p>
              </div>
            </div>
            <ProductScreenshot src="/img/sales-support-ai-branching.jpg" alt="GroupTalk実画面。ユーザーの発言に対してメイ、CFO、COO、弁護士が異なる立場から回答" width={802} height={1066} caption="役割やスタンスの異なるAIから、同じ提案への意見を受け取れます。" frameTitle="AI壁打ちルーム" cropClassName="aspect-[21/20] overflow-hidden" figureClassName="mx-auto w-full max-w-2xl" />
          </div>
          <div className="mx-auto max-w-4xl px-5 pb-16 text-center text-lg font-black leading-8 text-[#F8EDE6] md:px-8 md:pb-24">
            違う立場から意見をもらい、提案をブラッシュアップして本番へ。
          </div>
        </section>

        <section>
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro eyebrow="YOUR KNOWLEDGE" title="自社の提案書や資料も、営業準備の材料に。">
              <p>WizMateのナレッジライブラリには、自社の資料やメモを登録して、企業分析やAI壁打ちで参照できます。</p>
            </SectionIntro>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {KNOWLEDGE_ITEMS.map(({ title, lead, body }) => (
                <article key={title} className="rounded-[1.5rem] border border-[#E2CFC0] bg-white p-6 shadow-[0_12px_28px_rgba(88,61,47,0.08)]">
                  <h3 className="text-xl font-black text-[#4E3830]">{title}</h3>
                  <p className="mt-4 text-sm font-black leading-7 text-[#5D4037]">{lead}</p>
                  <p className="mt-3 text-sm leading-7 text-[#6B5851]">{body}</p>
                </article>
              ))}
            </div>
            <div className="mt-8 grid gap-4 rounded-[1.5rem] bg-[#F0E8DD] p-5 md:grid-cols-3 md:p-7">
              {KNOWLEDGE_FLOW_ITEMS.map(([title, body]) => (
                <div key={title} className="rounded-2xl bg-white/80 p-5">
                  <h3 className="font-black text-[#4E3830]">{title}</h3>
                  <p className="mt-2 text-sm leading-7 text-[#6B5851]">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="president-room" className="scroll-mt-20 bg-[#E8F0EB]">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-[1fr_0.85fr]">
            <SectionIntro eyebrow="AI PRESIDENT'S OFFICE" title="営業は、社長の仕事の一部だから。">
              <p>商談が終われば、また別の仕事が始まります。</p>
              <p className="mt-4">WizMateは営業だけのAIではありません。</p>
              <p className="mt-4"><strong className="text-[#355849]">相談する、調べる、資料を使う、考えを整理する。</strong></p>
              <p className="mt-4">社長の日々の仕事を、一つのAI環境で支える。</p>
              <p className="mt-4">それが「AI社長室 WizMate」です。</p>
            </SectionIntro>
            <div className="rounded-[2rem] bg-white p-6 shadow-[0_18px_42px_rgba(56,91,74,0.13)]">
              <p className="text-center text-xs font-black tracking-[0.18em] text-[#4E7C67]">AI社長室 WizMate</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {PRESIDENT_ROOM_FEATURES.map((item) => <div key={item} className="rounded-xl bg-[#F0F6F2] p-3 text-center text-xs font-bold leading-5 text-[#3F5E51]">{item}</div>)}
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <div className="rounded-[2rem] border border-[#E2CFC0] bg-white px-6 py-9 shadow-[0_15px_36px_rgba(102,72,56,0.08)] md:px-10 md:py-12">
              <SectionIntro eyebrow="TRUST" title="会社の資料をAIに入れても、大丈夫？" contentClassName="max-w-4xl">
                <p>営業資料や提案書には、社外に出したくない情報もあります。</p>
                <p className="mt-3">WizMateは回答や分析に必要な範囲で外部AIサービスを利用しますが、<strong className="text-[#4E3830]">ユーザーの明示的な同意がない限り、入力した会話や資料を外部AI事業者の汎用モデル学習へ任意提供しません。</strong></p>
                <p className="mt-3">WizMate側でも、明示的な同意なく、会話や資料を独自モデルの学習・再学習に使いません。</p>
                <p className="mt-3"><strong className="text-[#4E3830]">AIに処理させるために送信した情報が、そのまま一般に公開されたり、他のユーザーへ共有されたりするものではありません。</strong></p>
                <p className="mt-3">詳しいデータの取り扱いは、利用規約・プライバシーポリシーで確認できます。</p>
              </SectionIntro>
              <div className="mt-6 flex flex-wrap gap-5 text-sm font-black text-[#8A4F35]">
                <Link to={buildPublicLpDestinationHref("/privacy", returnTo)} className="underline underline-offset-4">プライバシーポリシーを見る</Link>
                <Link to={buildPublicLpDestinationHref("/terms", returnTo)} className="underline underline-offset-4">利用規約を見る</Link>
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="scroll-mt-20 bg-[#F8EFE8]">
          <div className="mx-auto max-w-5xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro
              eyebrow="PRICING"
              title={freePlan ? `まずは${freePlan.displayName}で、商談準備を試す。` : "商談準備に合うプランを確認する。"}
              align="center"
            >
              <p>料金と企業リサーチ回数は、現在のプラン設定をもとに表示しています。</p>
            </SectionIntro>
            {pricingReady ? (
            <div className="mx-auto mt-10 grid max-w-4xl gap-5 md:grid-cols-2">
              {[
                { key: "free" as const, plan: freePlan!, items: ["AI社長室を体験", `企業リサーチ ${freeCompanyResearchQuota}`, "通常AI相談・基本機能"], cta: "無料で始める" },
                { key: "standard" as const, plan: standardPlan!, items: [`企業リサーチ ${standardCompanyResearchQuota}`, "営業準備・AI壁打ち", "ナレッジ活用・通常AI相談"], cta: `まず${freePlan!.displayName}で試す` },
              ].map(({ key, plan, items, cta }) => (
                <article key={key} className={`rounded-[1.8rem] border bg-white p-7 shadow-[0_16px_36px_rgba(102,72,56,0.1)] ${key === "standard" ? "border-[#B87352]" : "border-[#E1CFC1]"}`}>
                  {key === "standard" ? <span className="rounded-full bg-[#8A4F35] px-3 py-1 text-[11px] font-black text-white">営業準備を続けるなら</span> : null}
                  <h3 className="mt-4 text-xl font-black text-[#4E3830]">{plan.displayName}</h3>
                  <p className="mt-2 text-2xl font-black text-[#8A4F35]">{plan.price.displayLabel}</p>
                  <p className="mt-3 text-sm leading-7 text-[#6B5851]">{plan.description}</p>
                  <ul className="mt-6 space-y-3 text-sm font-bold text-[#66534C]">
                    {items.map((item) => <li key={item} className="flex gap-2"><span className="text-[#B56847]">✓</span><span>{item}</span></li>)}
                  </ul>
                  <Link to={registrationHref} className={`mt-7 inline-flex min-h-12 w-full items-center justify-center rounded-xl px-5 text-sm font-black transition ${key === "standard" ? "bg-[#8A4F35] text-white hover:bg-[#74412C]" : "border border-[#B87352] text-[#8A4F35] hover:bg-[#FFF5EF]"}`}>{cta}</Link>
                </article>
              ))}
            </div>
            ) : (
              <div className="mx-auto mt-10 max-w-4xl">
                <ProductCatalogContentUnavailable
                  loading={catalogLoading}
                  retry={retryCatalog}
                  plansHref={buildPublicLpDestinationHref("/plans", returnTo)}
                />
              </div>
            )}
            <p className="mt-6 text-center text-xs leading-6 text-[#837068]">プランの全機能・注意事項は<Link to={buildPublicLpDestinationHref("/plans", returnTo)} className="ml-1 font-black text-[#8A4F35] underline underline-offset-4">料金ページ</Link>で確認できます。</p>
          </div>
        </section>

        <section className="px-5 py-16 md:px-8 md:py-24">
          <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2.2rem] bg-[linear-gradient(135deg,#714B3A_0%,#9A5D42_100%)] px-6 py-12 text-center text-white shadow-[0_24px_54px_rgba(107,67,48,0.25)] md:px-10 md:py-16">
            <div aria-hidden className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
            <h2 className="relative text-3xl font-black leading-tight md:text-4xl">次の商談を、WizMateで準備してみませんか？</h2>
            <div className="relative mx-auto mt-6 max-w-2xl space-y-2 text-base leading-8 text-[#F4E7E0]">
              <p>まず相手企業を調べる。自社の提案と照らす。AI専門家に気になるところをぶつける。</p>
              <p className="font-black text-white">本番までに、一度考えを整理する。</p>
              <p>その準備を、WizMateが一緒に進めます。</p>
            </div>
            <Link to={registrationHref} className="relative mt-8 inline-flex min-h-14 items-center justify-center rounded-2xl bg-white px-8 text-base font-black text-[#7A4C39] shadow-xl transition hover:bg-[#FFF6F1] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#8A4F35]">無料でWizMateを試す</Link>
          </div>
        </section>
      </main>
      <PublicLpFooter returnTo={returnTo} />
    </div>
  );
}

export default function SalesSupportPage() {
  const { catalog, loading, error, retry } = useProductCatalog();
  return (
    <SalesSupportPageView
      catalog={catalog}
      catalogLoading={loading}
      catalogError={error}
      retryCatalog={retry}
    />
  );
}
